import { requestEmail } from "@/models/api/authenticate.model";
import { Button } from "@/shadcn/components/ui/button";
import { Input } from "@/shadcn/components/ui/input";
import { validateEmail } from "@/utils/validation.util";
import { t } from "i18next";
import React, { useState } from "react";

type MagicLinkProps = {
  email: string;
  setEmail: React.Dispatch<React.SetStateAction<string>>;
};

const MagicLink: React.FC<MagicLinkProps> = ({ email, setEmail }) => {
  const [error, setError] = useState<string>("");
  const [magicLinkSent, setMagicLinkSent] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  const handleSendMagicLink = async () => {
    setError("");
    if (!validateEmail(email)) {
      setError("Please enter a valid email.");
      return;
    }

    setIsSubmitting(true);
    const response = await requestEmail(email);

    if (response === 202) {
      setMagicLinkSent(true);
    } else {
      if (response === 400) {
        setError("Invalid request. Please contact support.");
      } else if (response === 204) {
        setError("Invalid login. Please check your credentials");
      } else if (response === 401 || response === 403) {
        setError("Invalid login. Please check your credentials");
      }

      setError("Failed to send magic link.");
    }
    setIsSubmitting(false);
  };

  return (
    <div className="grid w-full gap-2">
      {!magicLinkSent ? (
        <div className="grid w-full gap-3">
          <div className="grid grid-cols-1 items-center">
            <Input
              type="text"
              className="w-full p-1 ps-2"
              id="email"
              placeholder="Email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoCapitalize="off"
            />
          </div>
          <div className="flex h-[36px] w-full items-center justify-center">
            <div className="text-center text-xs text-gray-500">
              Get a login link emailed — no password required.
            </div>
          </div>
          {error && (
            <div className="w-full text-sm break-words text-red-500">
              {error}
            </div>
          )}
          <Button
            onClick={handleSendMagicLink}
            className="w-full bg-neutral-800 text-gray-200 hover:bg-neutral-700"
            disabled={isSubmitting || email === ""}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                handleSendMagicLink();
              }
            }}
          >
            {isSubmitting ? (
              <div className="flex items-center justify-center gap-2">
                <span className="h-4 w-4 animate-spin rounded-full border-t-2 border-white"></span>
                {t("Sending...")}
              </div>
            ) : (
              t("Send Magic Link")
            )}
          </Button>
        </div>
      ) : (
        <div className="text-center">
          <p className="text-sm text-green-500">
            Magic link sent! Please check your email.
          </p>
        </div>
      )}
    </div>
  );
};

export default MagicLink;
